import { Branding } from '@cma/generated/graphql'
import { useEffect } from 'react'
import { BrandingFormData } from './types'

interface SelectedEntityLike {
  id: string
  brandableType: string
  [key: string]: unknown // Allow additional properties
}

export const createCroppedFile = async (
  originalFile: File,
  cropData: string
): Promise<File> => {
  return new Promise((resolve, reject) => {
    const img = new Image()
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')

    if (!ctx) {
      reject(new Error('Could not get canvas context'))
      return
    }

    img.onload = () => {
      try {
        const crop = JSON.parse(cropData)

        canvas.width = crop.width
        canvas.height = crop.height

        ctx.drawImage(
          img,
          crop.x,
          crop.y,
          crop.width,
          crop.height,
          0,
          0,
          crop.width,
          crop.height
        )

        canvas.toBlob(
          (blob) => {
            if (blob) {
              const croppedFile = new File([blob], originalFile.name, {
                type: originalFile.type,
                lastModified: Date.now()
              })
              resolve(croppedFile)
            } else {
              reject(new Error('Could not create blob from canvas'))
            }
          },
          originalFile.type,
          0.9
        )
      } catch (error) {
        reject(error)
      }
    }

    img.onerror = () => {
      reject(new Error('Could not load image'))
    }

    const objectUrl = URL.createObjectURL(originalFile)
    img.src = objectUrl
  })
}

export const processImagesWithCropping = async (
  logoFile: File | null,
  secondaryLogoFile: File | null,
  logoCropData?: string,
  secondaryLogoCropData?: string
): Promise<{
  processedLogoFile: File | null
  processedSecondaryLogoFile: File | null
}> => {
  let processedLogoFile = logoFile
  let processedSecondaryLogoFile = secondaryLogoFile

  // Process primary logo if it exists and has crop data
  if (logoFile && logoCropData) {
    try {
      processedLogoFile = await createCroppedFile(logoFile, logoCropData)
    } catch (error) {
      console.error('Error cropping primary logo:', error)
      // If cropping fails, use original file
    }
  }

  // Process secondary logo if it exists and has crop data
  if (secondaryLogoFile && secondaryLogoCropData) {
    try {
      processedSecondaryLogoFile = await createCroppedFile(
        secondaryLogoFile,
        secondaryLogoCropData
      )
    } catch (error) {
      console.error('Error cropping secondary logo:', error)
      // If cropping fails, use original file
    }
  }

  return { processedLogoFile, processedSecondaryLogoFile }
}

export const VALIDATION_RULES = {
  name: {
    required: 'Name is required',
    minLength: { value: 2, message: 'Name must be at least 2 characters' }
  },
  logoFile: {
    required: 'Logo is required'
  },
  secondaryLogoFile: {},
  primaryColor: {
    required: 'Primary color is required',
    pattern: {
      value: /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/,
      message: 'Please enter a valid hex color (e.g., #FF0000 or #F00)'
    }
  },
  primaryFont: {
    required: 'Primary font is required'
  },
  secondaryFont: {
    required: 'Secondary font is required'
  },
  secondaryColor: {
    pattern: {
      value: /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/,
      message: 'Please enter a valid hex color (e.g., #FF0000 or #F00)'
    }
  }
}

export const getValidationRules = (hasExistingLogo: boolean) => ({
  ...VALIDATION_RULES,
  logoFile: {
    required: hasExistingLogo ? false : 'Logo is required'
  },
  secondaryLogoFile: {}
})

export const DEFAULT_FORM_VALUES: BrandingFormData = {
  name: '',
  displayName: '',
  primaryColor: '#000000',
  secondaryColor: '',
  primaryFont: 'Arial',
  secondaryFont: 'Arial',
  logoFile: null,
  logoContentType: '',
  logoFileName: '',
  logoCropData: undefined,
  secondaryLogoFile: null,
  secondaryLogoContentType: '',
  secondaryLogoFileName: '',
  secondaryLogoCropData: undefined,
  locked: false
}

export const brandingToFormData = (
  branding: BrandingLike
): Partial<BrandingFormData> => {
  return {
    name: branding.name || DEFAULT_FORM_VALUES.name,
    displayName: branding.name || DEFAULT_FORM_VALUES.displayName,
    primaryColor: branding.primaryColor || DEFAULT_FORM_VALUES.primaryColor,
    secondaryColor:
      branding.secondaryColor || DEFAULT_FORM_VALUES.secondaryColor,
    primaryFont: branding.primaryFont || DEFAULT_FORM_VALUES.primaryFont,
    secondaryFont: branding.secondaryFont || DEFAULT_FORM_VALUES.secondaryFont,
    logoFile: null,
    logoContentType: DEFAULT_FORM_VALUES.logoContentType,
    logoFileName: branding.logoFileName || DEFAULT_FORM_VALUES.logoFileName,
    secondaryLogoFile: null,
    secondaryLogoContentType: DEFAULT_FORM_VALUES.secondaryLogoContentType,
    secondaryLogoFileName:
      branding.secondaryLogoFileName ||
      DEFAULT_FORM_VALUES.secondaryLogoFileName,
    locked: branding.locked || DEFAULT_FORM_VALUES.locked
  }
}

export const createBrandingInput = (
  data: BrandingFormData,
  logoFile: File | null,
  secondaryLogoFile: File | null,
  shouldRemoveSecondaryLogo = false,
  shouldRemovePrimaryLogo = false
) => ({
  name: data.name,
  locked: data.locked,
  logo: shouldRemovePrimaryLogo ? null : logoFile || undefined,
  secondaryLogo: shouldRemoveSecondaryLogo
    ? null
    : secondaryLogoFile || undefined,
  primaryColor: data.primaryColor || undefined,
  secondaryColor: data.secondaryColor || null,
  primaryFont: data.primaryFont || undefined,
  secondaryFont: data.secondaryFont || undefined
})

export const createNewBrandingInput = (
  data: BrandingFormData,
  logoFile: File | null,
  secondaryLogoFile: File | null,
  selectedEntity:
    | SelectedEntityLike
    | { id: string; brandableType: string; [key: string]: unknown },
  currentUserId: string,
  shouldRemoveSecondaryLogo = false,
  shouldRemovePrimaryLogo = false
) => ({
  ...createBrandingInput(
    data,
    logoFile,
    secondaryLogoFile,
    shouldRemoveSecondaryLogo,
    shouldRemovePrimaryLogo
  ),
  brandableId: selectedEntity.id,
  brandableType: selectedEntity.brandableType,
  published: true,
  createdBy: currentUserId
})

type BrandingLike = Pick<
  Branding,
  | 'name'
  | 'primaryColor'
  | 'secondaryColor'
  | 'primaryFont'
  | 'secondaryFont'
  | 'logoFileName'
  | 'secondaryLogoFileName'
  | 'locked'
>

export const getInitialFormValues = (
  existingBranding?: BrandingLike | null
) => {
  if (existingBranding) {
    return {
      ...DEFAULT_FORM_VALUES,
      ...brandingToFormData(existingBranding as Branding)
    }
  }
  return DEFAULT_FORM_VALUES
}

export const useAutoHideMessage = (
  message: string,
  setMessage: (msg: string) => void,
  delay = 5000
) => {
  useEffect(() => {
    if (message) {
      const timeout = setTimeout(() => {
        setMessage('')
      }, delay)
      return () => clearTimeout(timeout)
    }
  }, [message, setMessage, delay])
}
